"use client";

import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import type { VariantProps } from "class-variance-authority";

import { cn } from "./utils";
import { buttonVariants } from "./button";
import { HELD_TO_WINDOW, revealFocusThatMovesOn, revealTabThatMovedNothing } from "./held-to-window";
// 40px under a finger on a phone, and a label that wraps there: the step a
// plain dialog's footer button takes too.
import { PHONE_STEP } from "./phone-step";

/**
 * Which button a confirmation draws solid is the caller's to say. Confirming
 * is the solid one by default; a question whose safe answer is the way back
 * (a typed price that looks like a slip) turns the two round.
 */
type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

/**
 * A word longer than the dialog breaks. An address, or a name typed without
 * spaces, is one word, and it widened the dialog's one column the way a label
 * did: an address of 42 characters in a title by 119 to 208px on a phone. It
 * has to be `anywhere` and not `break-words`, which breaks the word only once
 * the box is too narrow for it and leaves it in the width the column asks
 * for, so the box never is. On the title and the description alone, not on
 * the whole dialog: the property is inherited, and what a caller puts inside
 * may size itself by its longest word (`SegmentedOptions`, `TabsList`).
 */
const LONG_WORD_BREAKS = "wrap-anywhere";

function AlertDialog({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

function AlertDialogTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return (
    <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
  );
}

function AlertDialogPortal({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return (
    <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
  );
}

function AlertDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Held to the window, scrolling down inside itself (`HELD_TO_WINDOW`, which
 * the plain dialog carries too). Six confirmations had each been given this by
 * hand after being found too tall (the Send dialog, 1,143px of it in a 768px
 * window); the other fifteen had nothing, and 9 of their 280 renders stood
 * past a 320 × 568px phone, 71 past one held sideways (7 Oct 2026). A caller's
 * own classes come after it and win, so a caller that lays its own scroll out
 * can; one that restates this, or takes it off, is failed by
 * `alert-dialog.test.tsx`.
 *
 * What it clips is whatever is wider than the dialog, which is why the buttons
 * below wrap on a phone.
 */
function AlertDialogContent({
  className,
  onFocus,
  onKeyUp,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          HELD_TO_WINDOW,
          className,
        )}
        onFocus={(event) => {
          onFocus?.(event);
          // Only a focus that moves on inside the dialog is brought into
          // view. The one it opens on arrives from outside and is the way
          // back, the last thing in the dialog on a phone, and scrolling to
          // it would open the question on its buttons with its title out of
          // view.
          revealFocusThatMovesOn(event);
        }}
        onKeyUp={(event) => {
          onKeyUp?.(event);
          // The way back is the one control of a confirmation whose go-ahead
          // is disabled, and a Tab there moves no focus.
          revealTabThatMovedNothing(event);
        }}
        {...props}
      />
    </AlertDialogPortal>
  );
}

function AlertDialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function AlertDialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn("text-lg font-semibold", LONG_WORD_BREAKS, className)}
      {...props}
    />
  );
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn("text-muted-foreground text-sm", LONG_WORD_BREAKS, className)}
      {...props}
    />
  );
}

function AlertDialogAction({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action> & {
  variant?: ButtonVariant;
}) {
  return (
    <AlertDialogPrimitive.Action
      className={cn(buttonVariants({ variant }), PHONE_STEP, className)}
      {...props}
    />
  );
}

function AlertDialogCancel({
  className,
  variant = "outline",
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel> & {
  variant?: ButtonVariant;
}) {
  return (
    <AlertDialogPrimitive.Cancel
      className={cn(buttonVariants({ variant }), PHONE_STEP, className)}
      {...props}
    />
  );
}

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
